/**
 * WCAG 2.1 contrast utilities for the theme SDK.
 *
 * Why this exists
 * ---------------
 * Hand-authored themes drift; AI-generated themes drift far more. The first
 * thing a user notices when a palette goes wrong is unreadable text. This
 * module is the single guardrail that keeps every applied theme above the
 * WCAG AA threshold for the foreground/background pairs that matter most.
 *
 * Two entry points:
 *   - `validateTheme(pack)` — pure inspection. Returns the failing pairs and
 *     their measured ratios so the UI can surface a contrast report.
 *   - `autoCorrectTheme(pack)` — returns a *new* pack with the offending
 *     foregrounds nudged lighter or darker (whichever direction restores
 *     contrast fastest) until every critical pair clears AA.
 *
 * Both operate on each color mode independently (light / dark) — a theme can
 * pass in one mode and fail in the other.
 *
 * Color math is on the standard sRGB → relative luminance → WCAG ratio path.
 * We work in HSL because that's the storage format, converting to RGB only
 * for the luminance computation.
 */
import type { ColorTokens, ThemeMode, ThemePack } from "@/theme/types";

/** WCAG AA body-text threshold. */
export const AA_NORMAL = 4.5;
/** WCAG AA large-text / non-text threshold. */
export const AA_LARGE = 3.0;

/**
 * Pairs we enforce on every theme. The list is intentionally narrow — these
 * are the surfaces where unreadable text would block a user from operating
 * the console (sign-in form, sidebar, primary buttons, destructive actions).
 *
 * `large` = true means we use the relaxed 3.0 threshold (decorative or
 * large-display text only). Body copy stays at 4.5.
 */
export interface ContrastPair {
  id: string;
  fg: keyof ColorTokens;
  bg: keyof ColorTokens;
  threshold: number;
  /** Human-readable label for issue lists in the UI. */
  label: string;
}

export const CRITICAL_PAIRS: ContrastPair[] = [
  { id: "body", fg: "foreground", bg: "background", threshold: AA_NORMAL, label: "Body text" },
  { id: "card", fg: "cardForeground", bg: "card", threshold: AA_NORMAL, label: "Card text" },
  { id: "popover", fg: "popoverForeground", bg: "popover", threshold: AA_NORMAL, label: "Popover text" },
  { id: "primary", fg: "primaryForeground", bg: "primary", threshold: AA_NORMAL, label: "Primary button" },
  { id: "secondary", fg: "secondaryForeground", bg: "secondary", threshold: AA_NORMAL, label: "Secondary button" },
  { id: "destructive", fg: "destructiveForeground", bg: "destructive", threshold: AA_NORMAL, label: "Destructive button" },
  { id: "accent", fg: "accentForeground", bg: "accent", threshold: AA_NORMAL, label: "Accent surface" },
  { id: "muted", fg: "mutedForeground", bg: "muted", threshold: AA_LARGE, label: "Muted helper text" },
  { id: "sidebar", fg: "sidebarForeground", bg: "sidebarBackground", threshold: AA_NORMAL, label: "Sidebar text" },
  { id: "sidebarActive", fg: "sidebarPrimaryForeground", bg: "sidebarPrimary", threshold: AA_NORMAL, label: "Sidebar active item" },
  { id: "success", fg: "successForeground", bg: "success", threshold: AA_NORMAL, label: "Success badge" },
  { id: "warning", fg: "warningForeground", bg: "warning", threshold: AA_NORMAL, label: "Warning badge" },
  { id: "info", fg: "infoForeground", bg: "info", threshold: AA_NORMAL, label: "Info badge" },
];

export interface ContrastIssue {
  pair: ContrastPair;
  ratio: number;
}

export interface ContrastReport {
  ok: boolean;
  /** Empty when `ok` is true. */
  issues: ContrastIssue[];
  /** Per-pair measurements regardless of pass/fail (handy for UI). */
  measurements: { pair: ContrastPair; ratio: number; passes: boolean }[];
}

// ---------------------------------------------------------------------------
// HSL ↔ RGB ↔ luminance helpers
// ---------------------------------------------------------------------------

/**
 * Parse an HSL string in the shorthand Tailwind expects: "H S% L%".
 * Returns null on anything that doesn't match — caller should treat that as
 * "skip this pair" rather than throwing.
 */
export function parseHsl(input: string): { h: number; s: number; l: number } | null {
  if (typeof input !== "string") return null;
  const match = input.trim().match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%$/);
  if (!match) return null;
  const h = Number(match[1]);
  const s = Number(match[2]);
  const l = Number(match[3]);
  if (Number.isNaN(h) || Number.isNaN(s) || Number.isNaN(l)) return null;
  return { h, s, l };
}

/** Format an HSL triple back into the shorthand "H S% L%" string we store. */
export function formatHsl(h: number, s: number, l: number): string {
  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s)}% ${round(l)}%`;
}

/** sRGB triple in 0..1 range. */
interface Rgb { r: number; g: number; b: number; }

/** Standard HSL → RGB. Adapted from CSS Color Level 4 reference impl. */
export function hslToRgb(h: number, s: number, l: number): Rgb {
  const sNorm = Math.max(0, Math.min(100, s)) / 100;
  const lNorm = Math.max(0, Math.min(100, l)) / 100;
  const hNorm = ((h % 360) + 360) % 360 / 60;

  const f = (n: number): number => {
    const k = (n + hNorm) % 12;
    const a = sNorm * Math.min(lNorm, 1 - lNorm);
    return lNorm - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return { r: f(0), g: f(8), b: f(4) };
}

/** Relative luminance per WCAG 2.x. Inputs are 0..1 sRGB. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.x contrast ratio, range 1..21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Convenience: ratio between two HSL strings, or null if either is unparseable. */
export function hslContrast(fg: string, bg: string): number | null {
  const fgHsl = parseHsl(fg);
  const bgHsl = parseHsl(bg);
  if (!fgHsl || !bgHsl) return null;
  const fgRgb = hslToRgb(fgHsl.h, fgHsl.s, fgHsl.l);
  const bgRgb = hslToRgb(bgHsl.h, bgHsl.s, bgHsl.l);
  return contrastRatio(fgRgb, bgRgb);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function evaluateMode(colors: ColorTokens): ContrastReport {
  const measurements: ContrastReport["measurements"] = [];
  const issues: ContrastIssue[] = [];

  for (const pair of CRITICAL_PAIRS) {
    const ratio = hslContrast(colors[pair.fg], colors[pair.bg]);
    if (ratio == null) continue; // unparseable → ignore (schema would have caught it)
    const passes = ratio >= pair.threshold;
    measurements.push({ pair, ratio, passes });
    if (!passes) issues.push({ pair, ratio });
  }

  return { ok: issues.length === 0, issues, measurements };
}

/**
 * Validate every mode the pack declares. The returned object is keyed by mode
 * so the UI can show separate light / dark contrast reports. A pack with one
 * failing mode still has a passing mode reported as `ok: true`.
 */
export function validateTheme(pack: ThemePack): {
  light?: ContrastReport;
  dark?: ContrastReport;
  ok: boolean;
} {
  const result: { light?: ContrastReport; dark?: ContrastReport; ok: boolean } = { ok: true };
  if (pack.light) {
    result.light = evaluateMode(pack.light.colors);
    if (!result.light.ok) result.ok = false;
  }
  if (pack.dark) {
    result.dark = evaluateMode(pack.dark.colors);
    if (!result.dark.ok) result.ok = false;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Auto-correct
// ---------------------------------------------------------------------------

/**
 * Nudge the foreground of a failing pair until it clears its threshold.
 *
 * Strategy: walk the foreground's L channel toward 0 or 100 (whichever
 * direction is *away* from the background's luminance) in 2% increments. We
 * cap iterations at 50 so a degenerate pair (e.g. mid-grey text on mid-grey
 * background where neither direction helps) gives up gracefully rather than
 * looping forever — caller will still see the issue in the post-correction
 * report and can fall back.
 */
function nudgeForegroundToward(
  fg: string,
  bg: string,
  threshold: number,
): string {
  const fgHsl = parseHsl(fg);
  const bgHsl = parseHsl(bg);
  if (!fgHsl || !bgHsl) return fg;

  const bgRgb = hslToRgb(bgHsl.h, bgHsl.s, bgHsl.l);
  const bgLum = relativeLuminance(bgRgb);

  // If background is dark we want a *lighter* foreground (push L toward 100),
  // and vice versa. The threshold of 0.179 is the standard sRGB midpoint
  // luminance recommended by the WCAG-on-text-color guidance.
  const direction = bgLum < 0.179 ? +1 : -1;

  let { l } = fgHsl;
  for (let i = 0; i < 50; i++) {
    const next = Math.max(0, Math.min(100, l + direction * 2));
    if (next === l) break; // hit a wall
    l = next;
    const candidate = hslToRgb(fgHsl.h, fgHsl.s, l);
    if (contrastRatio(candidate, bgRgb) >= threshold) {
      return formatHsl(fgHsl.h, fgHsl.s, l);
    }
  }
  // Couldn't reach threshold — pick the most contrasting endpoint as a last
  // resort. Pure white or near-black depending on background.
  const fallbackL = bgLum < 0.179 ? 98 : 4;
  return formatHsl(fgHsl.h, Math.min(fgHsl.s, 10), fallbackL);
}

function correctMode(mode: ThemeMode): ThemeMode {
  const colors = { ...mode.colors };
  for (const pair of CRITICAL_PAIRS) {
    const ratio = hslContrast(colors[pair.fg], colors[pair.bg]);
    if (ratio != null && ratio < pair.threshold) {
      colors[pair.fg] = nudgeForegroundToward(colors[pair.fg], colors[pair.bg], pair.threshold);
    }
  }
  return { colors, charts: mode.charts };
}

/** Returns a deep-cloned pack with foregrounds adjusted to clear AA. */
export function autoCorrectTheme(pack: ThemePack): ThemePack {
  return {
    ...pack,
    light: pack.light ? correctMode(pack.light) : undefined,
    dark: pack.dark ? correctMode(pack.dark) : undefined,
  };
}
